// Chart axis titles, rendered in reserved strips outside the plot area so
// long titles can never overlap the data. Used by both the line plot
// (vis/LinePlot) and the scatter plot (PanelScatterPlot's ScatterPlotChart).
import {THEME} from '@wandb/weave/common/css/color.styles';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {
  getAxisStyleForFontSize,
  type PlotFontSize,
  plotFontSizeToPx,
} from '../../util/plotHelpers/plotFontSize';
import {TruncateTextTooltip} from '../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';

const AXIS_TITLE_CHART_GAP_PX = 8;
const AXIS_TITLE_LINE_HEIGHT = 1.5;

type AxisTitles = {
  xTitle?: string;
  yTitle?: string;
};

/**
 * Thickness of the strips reserved for the axis titles outside the plot area,
 * so the titles can never overlap the data. Callers inset their plot cell by
 * these amounts; a side whose title is absent gets 0px, so the plot fills the
 * whole container.
 */
export function getAxisTitleInsets(
  fontSize: PlotFontSize,
  {xTitle, yTitle}: AxisTitles
): {left: number; bottom: number} {
  // A title's thickness (one line box, including ascender/descender room) plus
  // the chart gap. Derived from the font so the gap holds at every font size
  // and the title isn't clipped at `large`.
  const strip = getAxisTitleLineBoxPx(fontSize) + AXIS_TITLE_CHART_GAP_PX;
  return {
    left: yTitle ? strip : 0,
    bottom: xTitle ? strip : 0,
  };
}

function getAxisTitleLineBoxPx(fontSize: PlotFontSize): number {
  return Math.ceil(plotFontSizeToPx.axis[fontSize] * AXIS_TITLE_LINE_HEIGHT);
}

type AxisTitleStripsProps = AxisTitles & {
  fontSize: PlotFontSize;
  /**
   * Measured height of the plot cell, used as the rotated y-title's layout
   * width. The y title is skipped until it's known.
   */
  plotCellHeight?: number;
};

/**
 * The axis titles, rendered in the strips that `getAxisTitleInsets` reserves.
 * Positioned absolutely, so the caller must render this inside its positioned
 * container (the strips resolve against it, not against the plot cell).
 */
export const AxisTitleStrips: React.FC<AxisTitleStripsProps> = ({
  fontSize,
  xTitle,
  yTitle,
  plotCellHeight,
}) => {
  const insets = getAxisTitleInsets(fontSize, {xTitle, yTitle});
  const lineBoxPx = getAxisTitleLineBoxPx(fontSize);
  const fontPx = plotFontSizeToPx.axis[fontSize];
  // The axis tick-label style, borrowed only for its font family and color so
  // the titles match the ticks. Its font size (the same value, formatted as a
  // px string) is not reused — titleStyle wants the number from fontPx above.
  const tickStyle = {
    ...getAxisStyleForFontSize(fontSize),
    fill: THEME.textTertiary,
  } as unknown as ReturnType<typeof getAxisStyleForFontSize>;
  const fontFamily = tickStyle.fontFamily as React.CSSProperties['fontFamily'];
  // Computed at runtime, so these stay inline — everything positional is done
  // with Tailwind classes below.
  const titleStyle: React.CSSProperties = {
    fontSize: fontPx,
    fontFamily,
    color: tickStyle.fill as string,
    lineHeight: AXIS_TITLE_LINE_HEIGHT,
  };
  // CSS `font` shorthand matching the title style, passed to the tooltips so
  // their overflow detection measures text without a getComputedStyle DOM read
  // — that read forces a synchronous style flush per title on re-render (e.g.
  // when switching charts). We already know the font here.
  const font = `${fontPx}px ${fontFamily as string}`;

  // Tailwind classes need a `.tw-style` ancestor, which some callers only have
  // in part of their tree (the scatter panel lacks one in config mode) — the
  // wrapper scopes it to the titles and leaves the caller's markup untouched.
  // `display: contents` keeps the strips positioned against the caller's
  // container.
  return (
    <TailwindContents>
      {!!yTitle && (
        <div
          className="pointer-events-auto absolute left-0 top-0 overflow-hidden"
          style={{bottom: insets.bottom, width: insets.left}}>
          {plotCellHeight != null && (
            <div
              // Rotated block whose layout width is the plot cell's height, so
              // it spans the whole y axis and the tooltip measures overflow
              // against that length. `top-1/2` + `-translate-y-1/2` center it
              // along the axis; `left` (inline) + `-translate-x-1/2` put its
              // center half a line box in from the outer edge, so the text hugs
              // that edge and the gap sits on the chart-facing side.
              // pointer-events-auto so the tooltip can be hovered even under a
              // pointer-events:none ancestor.
              className="pointer-events-auto absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 -rotate-90 cursor-default whitespace-nowrap"
              style={{
                ...titleStyle,
                left: lineBoxPx / 2,
                width: plotCellHeight,
              }}>
              {/* The -90deg rotation maps the text's right edge to the top of
                  the plot, so `textAlign="right"` pins the title to the top of
                  the y axis. */}
              <TruncateTextTooltip
                className="w-full [&_*]:!cursor-default"
                fullText={yTitle}
                truncationType={TruncationType.Middle}
                textAlign="right"
                font={font}
                noLink
              />
            </div>
          )}
        </div>
      )}
      {!!xTitle && (
        <div
          // items-end pins the title to the bottom (outer) edge so the gap sits
          // above it, between the title and the chart. cursor-default keeps the
          // arrow (not the truncation button's hand).
          className="pointer-events-auto absolute bottom-0 right-4 flex cursor-default items-end whitespace-nowrap"
          style={{
            ...titleStyle,
            left: insets.left,
            height: insets.bottom,
          }}>
          {/* The tooltip reveals the full label on hover, only when actually
              truncated. `noLink` renders a plain (non-clickable) element rather
              than a Link. Font/color inherit from the container's titleStyle;
              `[&_*]:!cursor-default` overrides the pointer cursor the
              truncation element sets on itself. */}
          <TruncateTextTooltip
            className="w-full [&_*]:!cursor-default"
            fullText={xTitle}
            truncationType={TruncationType.Middle}
            textAlign="right"
            font={font}
            noLink
          />
        </div>
      )}
    </TailwindContents>
  );
};
